import { debounce } from 'lodash'
import { useEffect } from 'react'
import { queryClient } from 'v2/configs/rq-setup'
import {
  useAgentStatusUpdateMutation,
  useOrgStatusConfigQuery,
} from 'v2/pages/settings/org-status/queries'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { primaryTabManager } from 'v2/shared/utils/primary-tab-manager'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { useAppStore } from 'v2/state/app'
import { usePhoneStore } from 'v2/state/phone'
import { useTabStore } from 'v2/state/tab'
import type { ROrgStatus } from 'v2/types/api/settings'
import { PubnubStatus } from 'v2/types/pubnub'
import { setupMicrophonePermissionMonitor } from 'v2/utils'
import { logger } from 'v2/utils/logger'
import { phoneSDK } from 'v2/utils/phone-sdk'
import { sonnerManager } from '../../utils/sonner-manager'
import {
  activeCallToastConfig,
  idleStatusUpdateToastConfig,
  microphoneToastConfig,
  offlineToastConfig,
  phoneToastConfig,
  pubnubToastConfig,
} from './config'

export const PersistentSonnerHandler = () => {
  const isOnline = useAppStore((state) => state.isOnline)
  const pubnubStatus = useTabStore((state) => state.pubnubStatus)
  const isPhoneConnected = useTabStore((state) => state.isPhoneConnected)
  const isPhoneRegistered = usePhoneStore((state) => state.isRegistered)
  const agentStatus = useAppStore((state) => state.agentStatus)
  const { data: statusConfig } = useOrgStatusConfigQuery()
  const { mutate: updateAgentStatus } = useAgentStatusUpdateMutation()
  const isActiveCall = usePhoneStore((state) => state.isCallActive)
  const { data: profileData } = useProfileQuery()
  const humanSpecialistEnabled = profileData?.organizations?.[0]?.human_specialist_enabled ?? false

  // Show offline toast if the app is offline
  useEffect(() => {
    logger.info('PersistentSonnerHandler', 'isOnline', isOnline)
    sonnerManager.showToast(offlineToastConfig, !isOnline)
  }, [isOnline])

  useEffect(() => {
    // Idle/offline tracking belongs to the human-specialist surface only.
    if (!humanSpecialistEnabled) return

    const idleStatus = statusConfig?.idle_status?.value
    const statusName =
      queryClient
        .getQueryData<ROrgStatus[]>(getSettingsQueryKeys().status)
        ?.find((status) => status.uuid === idleStatus)?.status || 'idle'

    sonnerManager.showToast(
      idleStatusUpdateToastConfig(async () => {
        // On click update to login status
        const loginStatus = statusConfig?.sign_in_status.value
        if (loginStatus)
          updateAgentStatus({
            status_uuid: loginStatus,
            reason_for_change: 'manual update',
          })
        phoneSDK.connect([])
      }, statusName),
      agentStatus.updateType === 'idle',
    )
  }, [agentStatus.updateType, statusConfig, humanSpecialistEnabled])

  // Show pubnub toast if the pubnub is disconnected
  useEffect(() => {
    sonnerManager.showToast(pubnubToastConfig, pubnubStatus === PubnubStatus.DISCONNECTED)
  }, [pubnubStatus])

  // Show phone toast if the phone is not connected or registered
  useEffect(() => {
    debounce(() => {
      sonnerManager.showToast(
        phoneToastConfig,
        primaryTabManager.getIsPrimary() ? !isPhoneRegistered : !isPhoneConnected,
      )
      // Debounce to prevent showing the toast multiple times, and give the phone time to connect
    }, 2000)
  }, [isPhoneConnected, isPhoneRegistered])

  // Cleanup function and microphone toast
  useEffect(() => {
    setupMicrophonePermissionMonitor({
      onPermissionChange: (isDenied) => {
        sonnerManager.showToast(microphoneToastConfig, isDenied)
      },
    })
    return () => sonnerManager.dismissAll()
  }, [])

  useEffect(() => {
    sonnerManager.showToast(activeCallToastConfig, !!isActiveCall && !phoneSDK.isCallActive())
  }, [isActiveCall])

  return null
}
